import React, { useState } from "react";
import Chart from "react-apexcharts";
import { Card, CardBody, Button, Spinner } from "reactstrap";
import { loadReportsHelper } from "helpers/loadReports";
import formatCurrency from "constants/formatCurrency";

const MonthlyPerformanceChart = ({ user, included, selectedManager, selectedStructure }) => {
    const [loading, setLoading] = useState(false);
    const [chartData, setChartData] = useState({
        series: [],
        options: {
            chart: {
                toolbar: { show: false },
                zoom: { enabled: false },
            },
            colors: ['#22C55E', '#EF4444', '#10B981'], // Revenue (Green), Expense (Red), Profit (Dark Green)
            dataLabels: {
                enabled: true,
                formatter: function (val) {
                    return "$" + formatCurrency(val);
                },
                offsetY: -20,
                style: {
                    fontSize: '10px',
                    colors: ["#fff"]
                },
                background: {
                    enabled: true,
                    foreColor: '#000',
                    padding: 4,
                    borderRadius: 2,
                    borderWidth: 1,
                    borderColor: '#fff',
                    opacity: 0.9,
                },
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '55%',
                    endingShape: 'rounded',
                    dataLabels: {
                        position: 'top',
                    },
                },
            },
            stroke: {
                show: true,
                width: 2,
                colors: ['transparent']
            },
            xaxis: {
                categories: [], // Will be filled with Month names
                labels: {
                    style: { colors: '#fff' }
                }
            },
            yaxis: {
                labels: {
                    style: { colors: '#fff' },
                    formatter: (value) => { return "$" + formatCurrency(value) }
                },
            },
            legend: {
                position: 'top',
                labels: { colors: '#fff' }
            },
            grid: {
                borderColor: '#404040',
            },
            tooltip: {
                theme: 'light',
                y: {
                    formatter: function (val) {
                        return "$" + formatCurrency(val)
                    }
                }
            }
        }
    });

    const calculateMetrics = (reportsObj) => {
        const rows = Object.values(reportsObj).filter(data => {
            // Apply Filters locally if needed, or rely on helper if passed correctly
            if (selectedManager !== "All Managers" && (data.account_manager || "").toLowerCase() !== selectedManager.toLowerCase()) return false;
            if (selectedStructure !== "All Types" && (data.operating_structure || "").toLowerCase() !== selectedStructure.toLowerCase()) return false;
            return true;
        });

        const estMonthly = rows.reduce((sum, d) => sum + (d.daily_lease_amount || 0) * 30, 0);
        const totalBillables = rows.reduce((sum, d) => sum + (d.fees?.amount || 0), 0);

        const lotExpenses = rows.reduce((sum, d) =>
            sum + (d.costs?.payments || []).filter(p => p.type === "Parking Lot Cost").reduce((s, p) => s + (p.amount || 0), 0), 0
        );
        const totalLabor = rows.reduce((sum, d) => sum + (d.prospr?.cost || 0), 0);
        const totalPayables = rows.reduce((sum, d) => sum + (d.costs?.amount || 0), 0) - lotExpenses;

        const totalDataSources = rows.reduce((sum, d) => {
            const isLease = (d.operating_structure || "").toLowerCase() === "lease";
            const coreparkIncome = isLease ? 0 : (d.corepark?.cash || 0) + (d.corepark?.card || 0);
            return (
                sum + coreparkIncome + (d.spothero?.amount || 0) + (d.surepark?.amount || 0) +
                (d.resolve?.amount || 0) + (d.events?.income || 0) + (d.monthly_parking_orders?.amount || 0)
            );
        }, 0);

        const totalRevenue = totalBillables + estMonthly + totalDataSources;
        const totalExpense = totalPayables + lotExpenses + totalLabor;
        const netProfit = totalRevenue - totalExpense;

        return { totalRevenue, totalExpense, netProfit };
    };

    const handleLoadData = async () => {
        setLoading(true);
        const months = [
            { name: "August", start: "2025-08-01", end: "2025-08-31" },
            { name: "September", start: "2025-09-01", end: "2025-09-30" },
            { name: "October", start: "2025-10-01", end: "2025-10-31" },
            { name: "November", start: "2025-11-01", end: "2025-11-30" },
            { name: "December", start: "2025-12-01", end: "2025-12-31" },
        ];

        let revenueSeries = [];
        let expenseSeries = [];
        let profitSeries = [];
        let categories = [];

        try {
            // Process months sequentially to ensure order and manage load
            for (const month of months) {
                const reportsObj = await loadReportsHelper({
                    token: user.token,
                    start: month.start,
                    end: month.end,
                    included: included.includes("all") ? ["all"] : included,
                    selected: [{ value: "All Locations" }],
                    setProgressMessages: () => { }, // Suppress messages for this component
                    setLocations: () => { },
                    setActiveTab: () => { },
                });

                const { totalRevenue, totalExpense, netProfit } = calculateMetrics(reportsObj || {});

                revenueSeries.push(Math.round(totalRevenue));
                expenseSeries.push(Math.round(totalExpense));
                profitSeries.push(Math.round(netProfit));
                categories.push(month.name);
            }

            setChartData(prev => ({
                ...prev,
                series: [
                    { name: 'Revenue', data: revenueSeries },
                    { name: 'Expense', data: expenseSeries },
                    { name: 'Net Profit', data: profitSeries }
                ],
                options: {
                    ...prev.options,
                    xaxis: { ...prev.options.xaxis, categories }
                }
            }));

        } catch (error) {
            console.error("Error loading monthly graphic:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card style={{ backgroundColor: "#2f2f2f", borderRadius: 14, border: "1px solid rgba(255,255,255,0.1)" }}>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 style={{ color: "#fff", fontWeight: 700, margin: 0 }}>
                        Monthly Performance (Aug - Dec)
                    </h4>
                    <Button
                        color="warning"
                        onClick={handleLoadData}
                        disabled={loading}
                        style={{ fontWeight: "bold", color: "#000" }}
                    >
                        {loading ? <Spinner size="sm" /> : "Load Data"}
                    </Button>
                </div>

                <div id="monthly_chart" className="apex-charts" dir="ltr">
                    {chartData.series.length > 0 ? (
                        <Chart
                            options={chartData.options}
                            series={chartData.series}
                            type="bar"
                            height={400}
                        />
                    ) : (
                        <div style={{ textAlign: "center", padding: "50px", color: "#888" }}>
                            Click "Load Data" to view performance graphic.
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default MonthlyPerformanceChart;